import React from 'react';
import { MapPin } from 'lucide-react';
import AddressAutocomplete from '../map/AddressAutocomplete';
import type { Address, Coordinates } from '../../types';
import './NameProjectFields.css';

export type ProjectHubAddressFieldsValue = {
  address: Address;
  location: string;
  coordinates?: Coordinates;
};

interface ProjectHubAddressFieldsProps {
  value: ProjectHubAddressFieldsValue;
  onChange: (next: ProjectHubAddressFieldsValue) => void;
  disabled?: boolean;
  compact?: boolean;
}

const ProjectHubAddressFields: React.FC<ProjectHubAddressFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
}) => {
  const { address, location, coordinates } = value;

  const handleAutocompleteChange = (loc: string, addr?: Address, coords?: Coordinates) => {
    onChange({
      location: loc,
      address: addr ? { ...address, ...addr } : address,
      coordinates: coords ?? coordinates,
    });
  };

  const handleFieldChange = (field: keyof Address, fieldValue: string) => {
    onChange({
      ...value,
      address: { ...address, [field]: fieldValue },
      coordinates: undefined,
    });
  };

  return (
    <div
      className={`npf-root npf-address-fields${compact ? ' npf-root--compact npf-address-fields--compact' : ''}${disabled ? ' is-disabled' : ''}`}
    >
      <div className="npf-address-section-head">
        <label
          className="npf-label"
          title={compact ? 'Used for install weather on the timeline and team calendar.' : undefined}
        >
          <MapPin size={13} aria-hidden />
          Site address
        </label>
        {!compact && (
          <p className="npf-address-hint">
            Used for install weather on the timeline and team calendar.
          </p>
        )}
      </div>

      <div className={`npf-address-compact-grid${compact ? '' : ' npf-address-compact-grid--stacked'}`}>
        <div className="npf-row npf-address-search">
          {!compact && <span className="npf-address-sub-label">Search</span>}
          <AddressAutocomplete
            value={location}
            onChange={handleAutocompleteChange}
            placeholder={compact ? 'Search address…' : 'Type an address…'}
          />
        </div>

        {!compact && (
          <div className="npf-row npf-address-grid-full">
            <label className="npf-address-field-label" htmlFor="npf-site-street">
              Street
            </label>
            <input
              id="npf-site-street"
              type="text"
              className="npf-input"
              value={address.street}
              disabled={disabled}
              onChange={e => handleFieldChange('street', e.target.value)}
              placeholder="123 Main St"
            />
          </div>
        )}

        <div className="npf-row">
          <label className="npf-address-field-label" htmlFor="npf-site-city">
            City
          </label>
          <input
            id="npf-site-city"
            type="text"
            className="npf-input"
            value={address.city}
            disabled={disabled}
            onChange={e => handleFieldChange('city', e.target.value)}
          />
        </div>
        <div className="npf-row">
          <label className="npf-address-field-label" htmlFor="npf-site-state">
            State
          </label>
          <input
            id="npf-site-state"
            type="text"
            className="npf-input"
            value={address.state}
            disabled={disabled}
            onChange={e => handleFieldChange('state', e.target.value)}
          />
        </div>
        <div className="npf-row">
          <label className="npf-address-field-label" htmlFor="npf-site-zip">
            Zip
          </label>
          <input
            id="npf-site-zip"
            type="text"
            className="npf-input"
            value={address.zipCode}
            disabled={disabled}
            onChange={e => handleFieldChange('zipCode', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectHubAddressFields;
